/**
 * CountryCleanupPanel — one country, one directory group.
 *
 * Shows every country spelling that would be merged (trailing spaces, casing)
 * before anything is written, then applies the merge on one click.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Globe2, ArrowRight, CheckCircle2 } from 'lucide-react';

const show = (s) => (s === s.trim() ? s : `"${s}"`);

export const CountryCleanupPanel = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['church-country-audit'],
    queryFn: () => api.get('/admin/church-country-audit').then((r) => r.data),
  });

  const run = useMutation({
    mutationFn: () => api.post('/admin/church-country-audit/run'),
    onSuccess: (r) => {
      const n = r.data?.records_changed || 0;
      toast.success(n ? `Updated ${n} record${n === 1 ? '' : 's'}` : 'Nothing needed changing');
      qc.invalidateQueries({ queryKey: ['church-country-audit'] });
      qc.invalidateQueries({ queryKey: ['admin-churches'] });
      qc.invalidateQueries({ queryKey: ['church-locations'] });
      qc.invalidateQueries({ queryKey: ['church-directory-audit'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <Card data-testid="country-cleanup-panel">
        <CardContent className="py-10 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const fields = (data?.fields || []).filter((f) => f.groups_count > 0);
  const toChange = data?.records_to_change || 0;

  return (
    <Card data-testid="country-cleanup-panel">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-sky-600" />
            Place name cleanup
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Country, city, region and state spellings that differ only by spaces or casing
            become one directory group. The spelling most records already use is kept.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => run.mutate()}
          disabled={run.isPending || toChange === 0}
          data-testid="apply-country-cleanup"
        >
          {run.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          {toChange ? `Apply fixes (${toChange})` : 'Nothing to fix'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.length === 0 ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="country-cleanup-clean">
            <CheckCircle2 className="h-4 w-4" />
            Every place name is spelled one way — nothing to merge.
          </p>
        ) : (
          <div className="space-y-4" data-testid="country-cleanup-list">
            {fields.map((field) => (
              <div key={field.field} data-testid={`cleanup-field-${field.field}`}>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">
                  {field.field} · {field.records_to_change} record
                  {field.records_to_change === 1 ? '' : 's'} to change
                </p>
                <div className="border rounded-lg divide-y">
                  {field.groups.map((group) => (
                    <div key={`${field.field}-${group.canonical}`} className="p-3"
                         data-testid={`country-group-${group.canonical}`}>
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                        {group.canonical}
                        <Badge variant="outline" className="text-[10px]">
                          {group.total_records} record{group.total_records === 1 ? '' : 's'}
                        </Badge>
                      </div>
                      <ul className="mt-1.5 space-y-1">
                        {group.renames.map((rename) => (
                          <li key={rename.from}
                              className="text-xs text-slate-600 flex items-center gap-2">
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                              {show(rename.from)}
                            </span>
                            <ArrowRight className="h-3 w-3 text-slate-400" />
                            <span className="font-mono bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded">
                              {rename.to}
                            </span>
                            <span className="text-slate-400">
                              {rename.records} record{rename.records === 1 ? '' : 's'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {data?.last_run?.ran_at && (
          <p className="text-xs text-slate-500" data-testid="country-cleanup-last-run">
            Last cleanup {new Date(data.last_run.ran_at).toLocaleString()} by{' '}
            {data.last_run.actor || 'system'} · {data.last_run.records_changed || 0} updated
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CountryCleanupPanel;
